import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getProductListDataViewProperty } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import {
    CustomPropertyKey,
    CUSTOM_ATTRIBUTE_TYPE_PROPERTIES,
    CUSTOM_VARIANT_TRAIT_PROPERTIES,
    VARIANT_TRAIT_FACETS,
    INCLUDES_TEXT,
    MAXIMUM_QUANTITY_PER_PRODUCT,
} from "@thkStore/Constants/ProductPropertiesKeys";
import { CustomPropertiesByParentModel } from "@thkStore/Types/CustomPropertiesByParentModel";
import { VariantTraitFacetModel } from "@thkStore/Types/VariantChildrenGridModel";

/**
 * Parses custom properties from a JSON string in the data view properties.
 *
 * @param propertiesObj - Object containing the properties dictionary
 * @param propertyKey - The key to look up in the properties
 * @returns Parsed array of custom properties or empty array if not found
 */
function parseCustomPropertiesFromJson(
    propertiesObj: Record<string, string> | undefined,
    propertyKey: CustomPropertyKey
): CustomPropertiesByParentModel[] {
    if (!propertiesObj || !propertiesObj[propertyKey]) {
        return [];
    }

    try {
        return JSON.parse(propertiesObj[propertyKey]) as CustomPropertiesByParentModel[];
    } catch {
        return [];
    }
}

/**
 * Creates a selector for custom properties from the product list data view.
 * Use this for product list page contexts.
 *
 * @param propertyKey - The key identifying which custom properties to fetch
 * @returns Selector function for Redux state
 */
export function createProductListCustomPropertiesSelector(propertyKey: CustomPropertyKey) {
    return (state: ApplicationState): CustomPropertiesByParentModel[] => {
        const properties = getProductListDataViewProperty(state, "properties");
        if (!properties) {
            return [];
        }
        return parseCustomPropertiesFromJson(properties, propertyKey);
    };
}

/**
 * Creates a selector for custom properties from the product details data view.
 * Use this for product details page contexts with variant children.
 *
 * @param propertyKey - The key identifying which custom properties to fetch
 * @returns Selector function that takes state and productId
 */
function createProductDetailsCustomPropertiesSelector(propertyKey: CustomPropertyKey) {
    return (state: ApplicationState, productId: string): CustomPropertiesByParentModel[] => {
        const dataViewKey = `productId=${productId}&variantChildren=true`;
        
        const properties = state.data.products.dataViews[dataViewKey]?.properties;
        if (!properties) {
            return [];
        }
        const customProperties = parseCustomPropertiesFromJson(properties, propertyKey);        
        return customProperties;        
    };
}

function createProductDetailsCustomPropertySelector(propertyKey: CustomPropertyKey) {
    return (state: ApplicationState, productId: string): string | undefined => {
        const dataViewKey = `productId=${productId}&variantChildren=true`;
        const properties = state.data.products.dataViews[dataViewKey]?.properties;
        if (!properties) {
            return undefined;
        }
        return properties[propertyKey];
    };
}

function createProductDetailsCustomObjectPropertySelector(propertyKey: CustomPropertyKey) {
    return (state: ApplicationState, productId: string): any | undefined => {
        const dataViewKey = `productId=${productId}&variantChildren=true`;
        const properties = state.data.products.dataViews[dataViewKey]?.properties;
        if (!properties) {
            return undefined;
        }
        return JSON.parse(properties[propertyKey]);
    };
}

/** Selector for attribute type custom properties on product list page */
export const selectProductListAttributeTypeCustomProperties = createProductListCustomPropertiesSelector(
    CUSTOM_ATTRIBUTE_TYPE_PROPERTIES
);

/** Selector for attribute type custom properties on product details page */
export const selectProductDetailsAttributeTypeCustomProperties = createProductDetailsCustomPropertiesSelector(
    CUSTOM_ATTRIBUTE_TYPE_PROPERTIES
);

/** Selector for variant trait custom properties on product details page */
export const selectProductDetailsVariantTraitCustomProperties = createProductDetailsCustomPropertiesSelector(
    CUSTOM_VARIANT_TRAIT_PROPERTIES
);

export const selectProductDetailMaximumQuantityPerProduct = createProductDetailsCustomObjectPropertySelector(
    MAXIMUM_QUANTITY_PER_PRODUCT
);

/** Selector for variant trait facets on product details page */
export const selectProductDetailsVariantTraitFacets = (
    state: ApplicationState,
    productId: string,
): VariantTraitFacetModel[] => {
    const dataViewKey = `productId=${productId}&variantChildren=true`;
    const properties = state.data.products.dataViews[dataViewKey]?.properties;
    if (!properties || !properties[VARIANT_TRAIT_FACETS]) {
        return [];
    }
    try {
        return JSON.parse(properties[VARIANT_TRAIT_FACETS]) as VariantTraitFacetModel[];
    } catch {
        return [];
    }
};